import React from 'react';
import { createStyles, makeStyles, Theme } from '@material-ui/core';
import TextField, { TextFieldProps } from '@material-ui/core/TextField';
import { OutlinedInputProps } from '@material-ui/core/OutlinedInput';
import { getBackground } from '../../theme/styles';

export const appTextFieldRootStyle = (theme: Theme) => {
  const background = getBackground(theme.palette.type === 'dark');
  return {
    border: `1px solid ${background.backgroundInput}`,
    overflow: 'hidden',
    maxHeight: '56px',
    borderRadius: theme.shape.borderRadius,
    backgroundColor: background.backgroundInput,
    transition: theme.transitions.create(['border-color', 'box-shadow']),
    '&:hover, &$focused': {
      backgroundColor: theme.palette.background.paper,
      borderColor: theme.palette.primary.main,
    },
  };
};

export const appInputStyles = {
  padding: '0 24px 10px',
  marginTop: 27,
};

export const appSmallInputStyles = {
  padding: '10px 24px 10px',
};

export const useAppTextFieldStyles = makeStyles<Theme, { withoutLabel?: boolean }>(theme =>
  createStyles({
    root: appTextFieldRootStyle(theme),
    input: props => (props.withoutLabel ? appSmallInputStyles : appInputStyles),
    focused: {},
  })
);

export const AppTextField = React.forwardRef(
  ({ label, required, onChange, ...restProps }: TextFieldProps, ref) => {
    const classes = useAppTextFieldStyles({ withoutLabel: !label });
    const enrichLabel = required ? `${label}*` : label;
    const inputRef = React.useRef<HTMLInputElement>(null);

    const handleChange = (e: React.ChangeEvent<HTMLInputElement>) => {
      const input = inputRef.current;
      if (!input) return;
      // Let upstream handlers (e.g. masking/formatting libs) adjust the value/selection first.
      onChange?.(e);

      const selectionStart = input.selectionStart;
      const selectionEnd = input.selectionEnd;

      // Some controlled inputs re-render with a transformed value; restore the caret after paint.
      requestAnimationFrame(() => {
        if (selectionStart != null && selectionEnd != null) {
          input.setSelectionRange(selectionStart, selectionEnd);
        }
      });
    };

    return (
      <TextField
        innerRef={ref}
        inputRef={inputRef}
        variant="filled"
        label={enrichLabel}
        onChange={handleChange}
        {...restProps}
        InputProps={
          {
            classes,
            disableUnderline: true,
            ...restProps.InputProps,
          } as Partial<OutlinedInputProps>
        }
      />
    );
  }
);
